/**
 * Whether the BACKEND is actually reachable — as opposed to whether the OS
 * thinks it has a network interface.
 *
 * WHY THIS EXISTS (F-FE-N30, found in testing 2026-08-05)
 *
 * Offline capture was originally gated on `navigator.onLine`. That is wrong for
 * the outage restaurants actually have: the router stays up and the ISP goes
 * down, so the machine keeps a live network interface and `navigator.onLine`
 * reports `true` throughout. The gate never opened, and the cashier got a
 * "cannot reach the server" error instead of the offline flow — in exactly the
 * scenario the feature was built for.
 *
 * The desktop shell makes this sharper still. It serves the UI from localhost
 * and proxies /api/v1 to the tenant, so a dead internet connection surfaces as
 * a clean HTTP 502 `backend_unreachable` rather than a transport failure — a
 * real response, from a reachable server, about an unreachable one.
 *
 * `useOnline.ts` documented this trap; this module is what acts on it. The
 * authoritative signal is what happened to the last request, so that is what we
 * track: any transport failure or `backend_unreachable` marks the backend down,
 * and any successful response marks it up again.
 */

import { useEffect, useState } from 'react'

/** HTTP status the desktop proxy returns when it cannot reach the tenant. */
export const PROXY_UNREACHABLE_STATUS = 502
/** Stable code from electron/src/server.ts's proxy error handler. */
export const PROXY_UNREACHABLE_CODE = 'backend_unreachable'

let backendReachable = true
const listeners = new Set<(reachable: boolean) => void>()

function emit(next: boolean): void {
  if (next === backendReachable) return
  backendReachable = next
  for (const l of listeners) l(next)
}

/** Current belief about the backend. Starts optimistic — assume up until proven down. */
export function isBackendReachable(): boolean {
  return backendReachable
}

/** Any successful response proves the backend is up. */
export function markBackendReachable(): void {
  emit(true)
}

/**
 * Decide whether a failed request means the BACKEND is unreachable.
 *
 * Deliberately narrow. A 500 from a reachable server is a bug, not an outage,
 * and treating it as offline would send the cashier into offline capture over a
 * transient server error — printing tickets for orders that could have been
 * placed normally.
 *
 * Only two things count:
 *   - a transport failure (no response reached us at all), and
 *   - the desktop proxy's own 502 backend_unreachable.
 */
export function noteRequestFailure(err: {
  isNetworkError?: boolean
  status?: number
  code?: string
}): void {
  const transportFailed = err.isNetworkError === true
  const proxySaysDown =
    err.status === PROXY_UNREACHABLE_STATUS && err.code === PROXY_UNREACHABLE_CODE
  if (transportFailed || proxySaysDown) {
    emit(false)
    return
  }

  // F-FE-N78: a FAILED request can still prove the backend is there.
  //
  // Recovery used to depend on a 2xx, because markBackendReachable() is called
  // only on the success path. So a terminal that had gone offline stayed offline
  // through any amount of 401 / 409 / 500 traffic — every one of which is a real
  // HTTP response, from a reachable server, and therefore proof.
  //
  // That is not hypothetical. The two commonest replies to a reconnecting
  // terminal are a 409 `day_not_open` from the queue drain and a 401 from an
  // expired token; neither is 2xx, so the flag could stick indefinitely while
  // the connection was perfectly healthy. The offline queue drains on the
  // offline->online edge, so a stuck flag also means orders the kitchen already
  // cooked never sync — the exact symptom seen on 2026-08-27, with the banner
  // still up and everything sitting at "Pending sync".
  //
  // Anything with a status came back over the wire. The two cases above are the
  // only ones that mean otherwise, and both returned already.
  if (typeof err.status === 'number') emit(true)
}

/**
 * React binding. Combines the OS signal with the observed one: the terminal is
 * treated as offline if EITHER says so.
 *
 * navigator.onLine is kept because it is instant and correct when the adapter
 * genuinely drops — it just cannot be trusted on its own.
 */
export function useOnlineAndReachable(): { online: boolean; backendReachable: boolean } {
  const [osOnline, setOsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  const [reachable, setReachable] = useState(backendReachable)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const up = () => setOsOnline(true)
    const down = () => setOsOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    setOsOnline(navigator.onLine)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  useEffect(() => {
    const l = (v: boolean) => setReachable(v)
    listeners.add(l)
    setReachable(backendReachable)
    return () => {
      listeners.delete(l)
    }
  }, [])

  return { online: osOnline, backendReachable: reachable }
}

/** Test seam — resets module state between cases. */
export function __resetReachabilityForTests(): void {
  backendReachable = true
  listeners.clear()
}
